interface SeasonalPeriod {
  startMonth: number; // 1-12
  startDay: number;
  endMonth: number;
  endDay: number;
  promptKeys: string[];
}

// Seasonal periods ordered by date throughout the year
const SEASONAL_PERIODS: SeasonalPeriod[] = [
  // January 1-20: New Year / Winter Sale
  {
    startMonth: 1,
    startDay: 1,
    endMonth: 1,
    endDay: 20,
    promptKeys: ["new_year_1", "new_year_2"],
  },
  // January 21 - February 14: Valentine's Day
  {
    startMonth: 1,
    startDay: 21,
    endMonth: 2,
    endDay: 14,
    promptKeys: ["valentines_1", "valentines_2"],
  },
  // February 15 - March 8: Women's Day
  {
    startMonth: 2,
    startDay: 15,
    endMonth: 3,
    endDay: 8,
    promptKeys: ["womens_day_1", "womens_day_2"],
  },
  // March 9 - April 10: Spring / Easter
  {
    startMonth: 3,
    startDay: 9,
    endMonth: 4,
    endDay: 10,
    promptKeys: ["spring_1", "spring_2"],
  },
  // April 11 - May 26: Mother's Day
  {
    startMonth: 4,
    startDay: 11,
    endMonth: 5,
    endDay: 26,
    promptKeys: ["mothers_day_1", "mothers_day_2"],
  },
  // May 27 - June 21: Early Summer / Father's Day
  {
    startMonth: 5,
    startDay: 27,
    endMonth: 6,
    endDay: 21,
    promptKeys: ["early_summer_1", "early_summer_2"],
  },
  // June 22 - August 31: Summer Sale
  {
    startMonth: 6,
    startDay: 22,
    endMonth: 8,
    endDay: 31,
    promptKeys: ["summer_1", "summer_2"],
  },
  // September 1-30: Back to School
  {
    startMonth: 9,
    startDay: 1,
    endMonth: 9,
    endDay: 30,
    promptKeys: ["back_to_school_1", "back_to_school_2"],
  },
  // October 1-31: Halloween
  {
    startMonth: 10,
    startDay: 1,
    endMonth: 10,
    endDay: 31,
    promptKeys: ["halloween_1", "halloween_2"],
  },
  // November 1-28: Black Friday
  {
    startMonth: 11,
    startDay: 1,
    endMonth: 11,
    endDay: 28,
    promptKeys: ["black_friday_1", "black_friday_2"],
  },
  // November 29 - December 31: Christmas / New Year
  {
    startMonth: 11,
    startDay: 29,
    endMonth: 12,
    endDay: 31,
    promptKeys: ["christmas_1", "christmas_2"],
  },
];

const isDateInRange = (
  currentMonth: number,
  currentDay: number,
  startMonth: number,
  startDay: number,
  endMonth: number,
  endDay: number
): boolean => {
  const current = currentMonth * 100 + currentDay;
  const start = startMonth * 100 + startDay;
  const end = endMonth * 100 + endDay;

  if (start <= end) {
    return current >= start && current <= end;
  } else {
    // Year wrap case
    return current >= start || current <= end;
  }
};

export const getSeasonalPrompt = (): string => {
  const trans = window._trans._branding.aiBanner.seasonal_prompts;
  const now = new Date();
  const currentMonth = now.getMonth() + 1; // 1-12
  const currentDay = now.getDate();

  for (const period of SEASONAL_PERIODS) {
    if (isDateInRange(currentMonth, currentDay, period.startMonth, period.startDay, period.endMonth, period.endDay)) {
      const randomIndex = Math.floor(Math.random() * period.promptKeys.length);
      const key = period.promptKeys[randomIndex];
      return trans[key] || trans.fallback;
    }
  }

  return trans.fallback;
};

// Legacy function - now uses seasonal prompts
export const getRandomPrompt = (): string => {
  return getSeasonalPrompt();
};

const PROMPT_HISTORY_KEY = "ai_banner_prompt_history";
const MAX_HISTORY_ITEMS = 10;

export interface PromptHistoryItem {
  prompt: string;
  timestamp: number;
}

export const savePromptToHistory = (prompt: string): void => {
  if (!prompt.trim()) return;

  const existingHistory = getPromptHistory();
  const newItem: PromptHistoryItem = {
    prompt: prompt.trim(),
    timestamp: Date.now(),
  };

  const filteredHistory = existingHistory.filter((item) => item.prompt !== prompt.trim());
  const updatedHistory = [newItem, ...filteredHistory].slice(0, MAX_HISTORY_ITEMS);

  localStorage.setItem(PROMPT_HISTORY_KEY, JSON.stringify(updatedHistory));
};

export const getPromptHistory = (): PromptHistoryItem[] => {
  try {
    const stored = localStorage.getItem(PROMPT_HISTORY_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

export const clearPromptHistory = (): void => {
  localStorage.removeItem(PROMPT_HISTORY_KEY);
};
